import React, { useState, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    Select,
    MenuItem,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Pagination,
} from "@mui/material";
import { toast } from "react-toastify";
import { putData } from "../../../webService/webservice.js"; 

const ComboOfferAssignPopup = ({
    isOpen,
    onClose,
    products,
    categories,
    subCategories,
    selectedOfferId,
    fetchComboOffers,
    currentPage,
    limit,
}) => {
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedSubCategory, setSelectedSubCategory] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [productSearch, setProductSearch] = useState("");
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [page, setPage] = useState(1);
    const perPage = 5;
    const [selectAll, setSelectAll] = useState(false);

    // ✅ Filtering Logic
    useEffect(() => {
        let filtered = [...products];

        if (selectedCategory)
            filtered = filtered.filter((p) => p.categoryId === selectedCategory);

        if (selectedSubCategory)
            filtered = filtered.filter((p) => p.subCategoryId === selectedSubCategory);

        if (minPrice)
            filtered = filtered.filter(
                (p) => (p.sellingPrice || 0) >= parseFloat(minPrice)
            );

        if (maxPrice)
            filtered = filtered.filter(
                (p) => (p.sellingPrice || 0) <= parseFloat(maxPrice)
            );

        if (productSearch)
            filtered = filtered.filter(
                (p) =>
                    p.productCode?.toLowerCase().includes(productSearch.toLowerCase()) ||
                    p.title?.toLowerCase().includes(productSearch.toLowerCase())
            );

        setFilteredProducts(filtered);
        setPage(1);
    }, [
        selectedCategory,
        selectedSubCategory,
        minPrice,
        maxPrice,
        productSearch,
        products,
    ]);

    // ✅ Handle Single Product Select
    const handleSelectProduct = (productId) => {
        setSelectedProducts((prev) =>
            prev.includes(productId)
                ? prev.filter((id) => id !== productId)
                : [...prev, productId]
        );
    };

    // ✅ Handle Select All / Deselect All (for current page)
    const handleSelectAll = () => {
        const currentPageProducts = paginatedProducts.map((p) => p._id);
        if (selectAll) {
            setSelectedProducts((prev) =>
                prev.filter((id) => !currentPageProducts.includes(id))
            );
            setSelectAll(false);
        } else {
            const combined = Array.from(
                new Set([...selectedProducts, ...currentPageProducts])
            );
            setSelectedProducts(combined);
            setSelectAll(true);
        }
    };

    // ✅ Determine if all on page are selected
    useEffect(() => {
        const currentPageProducts = paginatedProducts.map((p) => p._id);
        const allSelected =
            currentPageProducts.length > 0 &&
            currentPageProducts.every((id) => selectedProducts.includes(id));
        setSelectAll(allSelected);
    }, [filteredProducts, selectedProducts, page]);

    // ✅ Pagination logic
    const startIndex = (page - 1) * perPage;
    const paginatedProducts = filteredProducts.slice(
        startIndex,
        startIndex + perPage
    );
    const totalPages = Math.ceil(filteredProducts.length / perPage);

    // ✅ Assign Products
    const handleAssignProducts = async () => {
        if (!selectedOfferId) {
            toast.error("No offer selected to assign products!");
            return;
        }

        if (selectedProducts.length === 0) {
            toast.error("Please select at least one product!");
            return;
        }

        try {
            setLoading(true);
            const token = sessionStorage.getItem("sessionToken");
            const payload = { productIds: selectedProducts };

            const res = await putData(
                `comboOffers/comboOffer/assignproducts/${selectedOfferId}`,
                payload,
                token
            );

            toast.success(res.message || "Products assigned successfully");

            if (fetchComboOffers) fetchComboOffers(currentPage, limit);
            onClose();
        } catch (error) {
            console.error("Error assigning products:", error);
            toast.error("Failed to assign products");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 700,
                    bgcolor: "white",
                    borderRadius: "12px",
                    boxShadow: 24,
                    p: 4,
                    maxHeight: "85vh",
                    overflowY: "auto",
                }}
            >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Assign Products to Combo Offer
                </Typography>

                {/* ✅ Counts Summary */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography variant="body2">
                        <strong>Total Products:</strong> {products.length} {" | "}
                        <strong>Filtered:</strong> {filteredProducts.length} {" | "}
                        <strong>Selected:</strong> {selectedProducts.length}
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectAll}
                                onChange={handleSelectAll}
                                disabled={filteredProducts.length === 0}
                            />
                        }
                        label={selectAll ? "Deselect All (This Page)" : "Select All (This Page)"}
                    />
                </Box>

                {/* ✅ Filters */}
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        flexWrap: "wrap",
                        marginBottom: "15px",
                    }}
                >
                    <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        displayEmpty
                        sx={{ flex: 1, minWidth: 150 }}
                    >
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map((c) => (
                            <MenuItem key={c._id} value={c._id}>
                                {c.categoryName}
                            </MenuItem>
                        ))}
                    </Select>

                    <Select
                        value={selectedSubCategory}
                        onChange={(e) => setSelectedSubCategory(e.target.value)}
                        displayEmpty
                        sx={{ flex: 1, minWidth: 150 }}
                    >
                        <MenuItem value="">All SubCategories</MenuItem>
                        {subCategories.map((s) => (
                            <MenuItem key={s._id} value={s._id}>
                                {s.subCategoryName}
                            </MenuItem>
                        ))}
                    </Select>

                    <TextField
                        label="Min Price"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        type="number"
                        sx={{ width: 120 }}
                    />
                    <TextField
                        label="Max Price"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        type="number"
                        sx={{ width: 120 }}
                    />
                    <TextField
                        label="Search"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                </div>

                {/* ✅ Product List (Paginated) */}
                <div
                    style={{
                        maxHeight: "45vh",
                        overflowY: "auto",
                        borderTop: "1px solid #eee",
                        borderBottom: "1px solid #eee",
                    }}
                >
                    {paginatedProducts.length === 0 ? (
                        <Typography
                            sx={{ textAlign: "center", mt: 2, color: "gray", fontSize: 14 }}
                        >
                            No products match your filters.
                        </Typography>
                    ) : (
                        paginatedProducts.map((p) => (
                            <Box
                                key={p._id}
                                onClick={() => handleSelectProduct(p._id)}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    borderBottom: "1px solid #eee",
                                    background: selectedProducts.includes(p._id)
                                        ? "#f4e7b3"
                                        : "transparent",
                                    cursor: "pointer",
                                    transition: "background 0.2s",
                                }}
                            >
                                <Typography>{p.productCode} — {p.title}</Typography>
                                <Typography>₹{p.sellingPrice}</Typography>
                            </Box>
                        ))
                    )}
                </div>

                {/* ✅ Pagination Controls */}
                {totalPages > 1 && (
                    <Box
                        sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, val) => setPage(val)}
                            color="primary"
                        />
                    </Box>
                )}

                {/* ✅ Buttons */}
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ background: "#8D2629" }}
                        disabled={loading}
                        onClick={handleAssignProducts}
                    >
                        {loading ? "Assigning..." : "Assign"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ComboOfferAssignPopup;
